import { ComponentType } from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { AlertDialogNoOkBtnComponent } from './alert-dialog-no-ok-btn/alert-dialog-no-ok-btn.component';
import { AlertDialogComponent } from './alert-dialog/alert-dialog.component';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { NotifyDialogComponent } from './notify-dialog/notify-dialog.component';

@Injectable()
export class DialogService {
    public constructor(private dialog: MatDialog,
                       private translateService: TranslateService,
    ) {
        // private viewContainerRef: ViewContainerRef,
        // private componentFactoryResolver: ComponentFactoryResolver) {
    }

    public confirm(title: string,
                   message: string,
                   messageClass?: string,
                   yesButtonText = this.translateService.instant('Confirm'),
                   noButtonText = this.translateService.instant('Cancel'),
                   dialogClass?: string,
    ): Observable<boolean> {
        const config: MatDialogConfig = {
            width: '600px',
            panelClass: 'mobile-fullscreen-dialog',
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, config);
        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.message = message;
        dialogRef.componentInstance.messageClass = messageClass;
        dialogRef.componentInstance.yes = yesButtonText;
        dialogRef.componentInstance.cancel = noButtonText;
        dialogRef.componentInstance.dialogClass = dialogClass;

        return dialogRef.afterClosed();
    }

    public alert(title: string, message: string): Observable<boolean> {
        const dialogRef = this.dialog.open(AlertDialogComponent);
        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.message = message;

        return dialogRef.afterClosed();
    }

    public notify(title: string, ...messages: string[]): Observable<boolean> {
        const config: MatDialogConfig = {
            panelClass: 'mobile-fullscreen-dialog',
        };

        const dialogRef = this.dialog.open(NotifyDialogComponent, config);
        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.messages = messages;

        return dialogRef.afterClosed();
    }

    public alertNoOkBtn(title: string, message: string): Observable<boolean> {
        const dialogRef = this.dialog.open(AlertDialogNoOkBtnComponent);
        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.message = message;

        return dialogRef.afterClosed();
    }

    // public loadComponent<T>(dialogComponent: { new(): T }, dialogConfig: MatDialogConfig): Observable<any> {
    //   let dialogRef: MatDialogRef<T>;
    //
    //   dialogRef = this.dialog.open(this.createDialog<T>(dialogComponent), dialogConfig);
    //
    //   return dialogRef.afterClosed();
    // }
    //
    // private createDialog<T>(dialogComponent: { new(): T }): ComponentRef<T> {
    //   this.viewContainerRef.clear();
    //
    //   const dialogComponentFactory = this.componentFactoryResolver.resolveComponentFactory(dialogComponent);
    //   return this.viewContainerRef.createComponent(dialogComponentFactory);
    // }

    /**
     * Opens a `MatDialog` with the specified component and passes data to it.
     * The dialog will return a value of type `R` when closed, which can be observed
     * through the returned `Observable<R>`. The component can inject public dialogRef: MatDialogRef<ComponentSelf>
     * in its constructor and use it to emit a result upon closing the dialog.
     *
     * @template T - The type of the component that will be opened in the dialog.
     * @template R - The type of data that will be emitted when the dialog is closed (default is `unknown`).
     *
     * @param component - The Angular component to be rendered in the dialog.
     *                    This component will be dynamically created inside the `MatDialog`.
     * @param data - The data to pass into the component via the `MAT_DIALOG_DATA` injection token.
     *               This data will be available to the component that is opened.
     * @param config - The MatDialogConfig to be merged with the default dialog config defined in the function.
     *
     * @returns An `Observable<R>` that emits when the dialog is closed. The observable
     *          will emit the value passed to `dialogRef.close(result)` inside the dialog component.
     *
     * @example
     * // Opening a dialog with MyDialogComponent and expecting a string result.
     * this.dialogService.open<MyDialogComponent, string>(MyDialogComponent, { someData: 'example' })
     *   .subscribe((result: string | undefined) => {
     *     if (result) {
     *       console.log('Dialog closed with result:', result);
     *     }
     *   });
     *
     * @example
     * // Opening a dialog with MyDialogComponent and expecting a complex object as result.
     * interface IDialogResult {
     *   confirmed: boolean;
     *   additionalInfo: string;
     * }
     * this.dialogService.open<MyDialogComponent, IDialogResult>(MyDialogComponent, { someData: 'example' })
     *   .subscribe((result: IDialogResult | undefined) => {
     *     if (result) {
     *       console.log('Confirmed:', result.confirmed);
     *     }
     *   });
     */
    public open<T, R = unknown>(component: ComponentType<T>, data: unknown, config?: MatDialogConfig): Observable<R> {
        const defaultConfig: MatDialogConfig = {
            panelClass: 'mobile-fullscreen-dialog',
            minWidth: '20rem',
            minHeight: '20rem',
        };

        const mergedConfig = Object.assign(defaultConfig, config);
        mergedConfig.data = data;

        const dialogRef = this.dialog.open(component, mergedConfig);

        return dialogRef.afterClosed() as Observable<R>;
    }
}
